// Model peran AURA PPDS:
//  super_admin  - akses penuh, termasuk aturan sistem & hapus permanen
//  admin_ufp    - operasional semua prodi, TANPA Pengaturan Periode & hapus permanen
//  admin_prodi  - hanya prodinya sendiri (row-level security)
//  admin_koas   - seperti admin_prodi utk SATU Modul (prodi berjenjang Koas, users.prodi_id);
//                 akun lama tanpa modul tetap lintas SEMUA prodi Koas. Di layar "Prodi" -> "Modul"
//                 (istilahProdi).
//  admin_instalasi - HANYA-BACA Monitoring Rotasi Klinik "Per Instalasi" utk instalasinya
//                 sendiri (users.instalasi_ids) + Keamanan Akun. Server menolak semua
//                 rute lain (ADMIN_INSTALASI_RUTE di server.py); di sini hanya navigasinya.

export const ROLE_LABEL = {
    super_admin: "Super Admin",
    admin_ufp: "Admin UFP",
    admin_prodi: "Admin Prodi",
    admin_koas: "Admin Koas",
    admin_instalasi: "Admin Instalasi",
};

// Teks gelap (#0F2E30) wajib di atas lime & turquoise medium — putih di atas warna itu
// gagal AA (1.51:1 dan 2.11:1). super_admin = lime (paling menonjol), admin_ufp = turquoise
// medium ("warna sekunder... badge" pada palet resmi), keduanya beda dari sidebar (teal).
export const ROLE_BADGE_CLASS = {
    super_admin: "bg-[#CDDC29] hover:bg-[#CDDC29] text-[#0F2E30]",
    admin_ufp: "bg-[#60C0D0] hover:bg-[#60C0D0] text-[#0F2E30]",
    admin_prodi: "",
    admin_koas: "",
    admin_instalasi: "",
};

// Melihat/mengubah data lintas prodi tanpa row-level security.
export const seesAllProdi = (role) => role === "super_admin" || role === "admin_ufp";

// Tab Rotasi Klinik "Per Peserta" <-> "Per Instalasi" (hanya-baca). Admin Prodi & Admin Koas sejak Okt 2026 --
// datanya DIBATASI SERVER ke cakupan prodinya (_cakupan_prodi_per_instalasi: Admin Prodi = prodinya,
// Admin Koas = semua prodi Koas). Admin Instalasi memakai halaman Per Instalasi tanpa tab (menunya
// langsung ke sana). Daftar putih, bukan negasi.
export const PERAN_TAB_PER_INSTALASI = ["super_admin", "admin_ufp", "admin_prodi", "admin_koas"];
export const bisaTabPerInstalasi = (role) => PERAN_TAB_PER_INSTALASI.includes(role);
export const isAdminProdi = (role) => role === "admin_prodi";

// Input Rotasi Klinik (dialog lama & panel Input Rotasi Cepat) -- sama dgn POST /rotasi-klinik: peran yang
// lolos require_active_user + _scope_query. Admin Instalasi hanya-baca. Daftar putih, bukan negasi.
export const PERAN_INPUT_ROTASI = ["super_admin", "admin_ufp", "admin_prodi", "admin_koas"];
export const bisaInputRotasi = (role) => PERAN_INPUT_ROTASI.includes(role);

// Periode Cuti peserta di dialog Edit Peserta -- cermin PERIODE_CUTI_ROLES (PUT /peserta/{id}/periode-cuti):
// Admin Prodi/Koas tetap dibatasi cakupan & jendela edit oleh server. Daftar putih, bukan negasi.
export const PERAN_PERIODE_CUTI = ["super_admin", "admin_ufp", "admin_prodi", "admin_koas"];
export const bolehUbahPeriodeCuti = (role) => PERAN_PERIODE_CUTI.includes(role);

// Sinkronisasi Stase dari Rotasi: kartu, pratinjau, "Jalankan sekarang", dan kunci per peserta -- cermin require_ufp_or_super.
// Saklar hanya Super Admin (require_super_admin). Daftar putih, bukan negasi.
export const PERAN_SINKRON_STASE = ["super_admin", "admin_ufp"];
export const bolehKelolaSinkronStase = (role) => PERAN_SINKRON_STASE.includes(role);
export const bolehUbahSaklarSinkronStase = (role) => role === "super_admin";

// Hanya Super Admin (aturan sistem, hapus permanen).
export const isSuperAdmin = (role) => role === "super_admin";

// Indikator Keaktifan Akun (halaman /keaktifan): daftar putih Super Admin & Admin UFP -- cermin
// require_ufp_or_super di GET /admin/keaktifan. Kolom "Terakhir login" di Akun Admin tetap khusus Super Admin.
export const PERAN_KEAKTIFAN = ["super_admin", "admin_ufp"];
export const bolehLihatKeaktifan = (role) => PERAN_KEAKTIFAN.includes(role);

// Opsi impor "izinkan biodata belum lengkap" -- HANYA Super Admin (backend menolak 403 utk
// peran lain lewat BIODATA_LONGGAR_ROLES; di sini cuma menentukan kotak centang tampil/tidak).
export const bolehImporBiodataLonggar = (role) => role === "super_admin";

// Admin Koas -- row-level security lintas prodi berbasis jenjang (lihat lib/roles.js
// header & backend _scope_query/_koas_prodi_ids di server.py).
export const isAdminKoas = (role) => role === "admin_koas";

// Istilah "Prodi" per peran -- SATU sumber teks. Admin Koas melihat "Modul" (satu akun = satu Modul Koas);
// peran lain tetap "Prodi" / "Program Studi". Hanya teks layar: nama field, API, & nama prodi di data tetap.
//   singkat: "Prodi" | "Modul"   panjang: "Program Studi" | "Modul"   kecil: "prodi" | "modul"
const ISTILAH_PRODI = { singkat: "Prodi", panjang: "Program Studi", kecil: "prodi" };
const ISTILAH_MODUL = { singkat: "Modul", panjang: "Modul", kecil: "modul" };
export const istilahProdi = (role) => (isAdminKoas(role) ? ISTILAH_MODUL : ISTILAH_PRODI);

// Modul akun Admin Koas (prodi_id), atau null utk akun lama tanpa modul / peran lain.
export const modulKoas = (user) => (isAdminKoas(user?.role) && user?.prodi_id) || null;

// Boleh menghapus (soft-delete) peserta didik -- kebijakan 26 Sep 2026: khusus
// Super Admin (sebelumnya semua role kecuali Admin UFP). Peserta yg dihapus TIDAK
// hilang dari DB, cuma disembunyikan & bisa dipulihkan lewat halaman "Peserta
// Terhapus" (jg khusus Super Admin, lihat isSuperAdmin).
export const canDeletePeserta = (role) => role === "super_admin";

// Boleh kelola/monitor menu admin Peer Assessment Budaya CIPTO -- kebijakan tahap
// awal (27 Sep 2026): khusus Super Admin, supaya modul bisa dipelajari & diuji dulu
// sebelum dibuka ke role lain. SATU-SATUNYA tempat yg perlu diubah nanti kalau mau
// tambah Admin Prodi/Admin UFP -- backend punya fungsi cermin (_can_manage_peer_
// assessment di server.py) yg juga cuma perlu diubah di satu tempat itu.
// Form pengisian PPDS TIDAK lewat sini sama sekali -- itu publik tanpa akun AURA.
export const canManagePeerAssessment = (role) => role === "super_admin";

// Survei Kepuasan Peserta Didik (menu & halaman /survei-kepuasan): daftar putih, saat ini hanya Super Admin.
export const PERAN_KELOLA_SURVEI = ["super_admin"];
export const bolehKelolaSurvei = (role) => PERAN_KELOLA_SURVEI.includes(role);

// Admin Instalasi -- lihat header file ini.
export const isAdminInstalasi = (role) => role === "admin_instalasi";

export const HALAMAN_PER_INSTALASI = "/rotasi-klinik/dashboard-instalasi";
export const HALAMAN_KEAMANAN_AKUN = "/pengaturan/keamanan-akun";
// Satu-satunya halaman yg boleh dibuka Admin Instalasi; selain itu dialihkan ke halaman awalnya.
export const RUTE_ADMIN_INSTALASI = [HALAMAN_PER_INSTALASI, HALAMAN_KEAMANAN_AKUN];

// Halaman tujuan setelah login (dan saat membuka halaman yg tidak diizinkan).
export const halamanAwal = (role) => (isAdminInstalasi(role) ? HALAMAN_PER_INSTALASI : "/dashboard");

// Peran tanpa batasan halaman (selain roles=[...] per rute) selalu true.
export const halamanDiizinkan = (role, path) =>
    !isAdminInstalasi(role) || RUTE_ADMIN_INSTALASI.includes(String(path || "").replace(/\/+$/, ""));

// Reset kata sandi dari halaman Keaktifan Akun: KHUSUS Admin UFP (keputusan pemilik 17 Okt 2026), hanya utk baris
// yg server tandai `boleh_direset` (Admin Prodi/Koas aktif). Super Admin memakai Akun Admin (/users).
export const bolehResetSandiDariKeaktifan = (role) => role === "admin_ufp";
